"use client"

import { useEffect } from "react"
import { usePathname, useRouter } from "next/navigation"

import { isAllBooksId } from "@/lib/all-books"
import { useFetchCurrentUser } from "@/hooks/api/users"
import { UpgradeSuccessDialog } from "@/components/billing/upgrade-success-dialog"
import { DocumentEditorDialogRestorer } from "@/components/document-editor/document-editor-dialog-restorer"
import LayoutContainer from "@/components/layout/layout-container"
import { CommandPaletteGlobalActionsInitializer } from "@/components/providers/command-palette-wrapper"

export default function BookLayout({
  children,
}: Readonly<{
  children: React.ReactNode
}>) {
  const { isLoading, data } = useFetchCurrentUser()
  const pathname = usePathname()
  const router = useRouter()

  const currentBookId = pathname.split("/")[1]
  // A one-book user should never sit on the All Books sentinel: the switcher is
  // hidden and book-only nav is disabled, leaving no path back to their book.
  const soleBook = data && data.books.length === 1 ? data.books[0] : undefined
  const mustLeaveAllBooks = Boolean(soleBook) && isAllBooksId(currentBookId)

  useEffect(() => {
    if (!soleBook || !isAllBooksId(currentBookId)) return
    const rest = pathname.split("/").slice(2).join("/")
    router.replace(`/${soleBook.id}/${rest}`)
  }, [soleBook, currentBookId, pathname, router])

  if (isLoading || !data || mustLeaveAllBooks) return null

  return (
    <>
      <CommandPaletteGlobalActionsInitializer />
      {/^\/[^/]+\/dashboards\/[^/]+\/edit\/?$/.test(pathname) ? (
        <main className="bg-background flex h-dvh flex-col overflow-hidden">{children}</main>
      ) : (
        <LayoutContainer>{children}</LayoutContainer>
      )}
      <UpgradeSuccessDialog />
      <DocumentEditorDialogRestorer />
    </>
  )
}
